import { useCallback, useEffect, useRef, useState } from "react";
import { useWallet } from "../context/WalletContext";
import { useContract } from "./useContract";
import { useToast } from "../components/common/ToastContext";
import { ensureAllowance, getUsdtBalance } from "../utils/usdt";
import { parseError } from "../utils/errors";
import { parseUsdt } from "../utils/format";
import { DEPLOY_BLOCK, LOG_CHUNK } from "../config/contract";

const EMPTY = {
  userId: 0,
  registered: false,
  details: null, // { account, id, referrer, isActive, totalDirectReferrals, claimableAmount, totalClaimed, totalIncome }
  breakdown: null,
  packages: Array(12).fill(false),
  packageDetails: Array(12).fill(null),
  prices: Array(12).fill(null),
  referrals: [],
  clubInfo: null,
  eligibility: { royalty: false, gold: false, diamond: false, global: false },
  claimableGold: null,
  claimableDiamond: null,
  claimableGlobal: null,
  usdtBalance: null,
  held: Array(12).fill(null), // per-board { heldCount, heldAmount, pending, resolved }
  highestBoard: 0,
  levelsUnlocked: 0,
  activities: [], // [{ type, label, amount, board, key }]
  isAdmin: false,
  isOwner: false,
  heldLoaded: false,
  activitiesLoaded: false,
  minWithdraw: null,
};

const INCOME_LABELS = ["Direct", "Upgrade", "Level", "Royalty", "Gold", "Diamond", "Global"];

export function useNexaura() {
  const { account, isCorrectNetwork, getProvider, getSigner, refreshKey, refresh } = useWallet();
  const { read, write } = useContract();
  const toast = useToast();

  const [data, setData] = useState(EMPTY);
  // Start in loading when an account is already present so the boot loader
  // shows instead of a flash of the Register screen.
  const [loading, setLoading] = useState(Boolean(account));
  const [busy, setBusy] = useState(false); // a write tx is in flight
  const inflight = useRef(false);

  // ---- Recent activity scan (deploy-block floor, RPC-safe chunks) --------
  const scanActivities = useCallback(
    async (userId) => {
      try {
        const latest = await read.provider.getBlockNumber();
        const floor = Math.max(0, DEPLOY_BLOCK);
        const incF = read.filters.IncomeCredited(userId);
        const wdF = read.filters.Withdrawn(userId);
        const upF = read.filters.AutoUpgraded(userId);

        const events = [];
        const MAX_CHUNKS = 50;
        let to = latest;
        let chunks = 0;
        while (to >= floor && chunks < MAX_CHUNKS && events.length < 12) {
          const from = Math.max(floor, to - LOG_CHUNK + 1);
          const [a, b2, c] = await Promise.all([
            read.queryFilter(incF, from, to),
            read.queryFilter(wdF, from, to),
            read.queryFilter(upF, from, to),
          ]);
          events.push(...a, ...b2, ...c);
          if (from === floor) break;
          to = from - 1;
          chunks++;
        }

        // Approx timestamp from block height (BSC ~3s/block) for the trend chart.
        const nowMs = Date.now();
        const tsOf = (bn) => nowMs - (latest - bn) * 3000;

        const items = [];
        events.forEach((e) => {
          const ts = tsOf(e.blockNumber);
          if (e.event === "IncomeCredited") {
            items.push({
              type: "income",
              label: `${INCOME_LABELS[e.args.incomeType] || "Income"} income`,
              amount: e.args.amount,
              board: e.args.boardId.toNumber(),
              blockNumber: e.blockNumber,
              ts,
              key: `${e.transactionHash}-${e.logIndex}`,
            });
          } else if (e.event === "Withdrawn") {
            items.push({ type: "withdraw", label: "Withdrawal", amount: e.args.net, board: 0, blockNumber: e.blockNumber, ts, key: `${e.transactionHash}-${e.logIndex}` });
          } else if (e.event === "AutoUpgraded") {
            items.push({ type: "upgrade", label: `Auto-upgraded to Board ${e.args.toBoard.toNumber()}`, amount: e.args.usedHeld, board: e.args.toBoard.toNumber(), blockNumber: e.blockNumber, ts, key: `${e.transactionHash}-${e.logIndex}` });
          }
        });
        items.sort((x, y) => y.blockNumber - x.blockNumber);
        // Keep all income items for the chart; activities list will slice later.
        return items;
      } catch {
        return [];
      }
    },
    [read]
  );

  // ---- Load all on-chain data (guarded; never loops) ---------------------
  const load = useCallback(async () => {
    if (!account) {
      setData(EMPTY);
      return;
    }
    if (inflight.current) return;
    inflight.current = true;
    setLoading(true);
    try {
      const provider = getProvider();

      // Fetch prices + roles + userId + minWithdraw in parallel (fast first paint).
      const [prices, ownerAddr, adminAddr, userIdBn, minW] = await Promise.all([
        Promise.all(Array.from({ length: 12 }, (_, i) => read.packagePrices(i))),
        read.owner().catch(() => null),
        read.admin().catch(() => null),
        read.addressToId(account),
        read.minWithdraw().catch(() => null),
      ]);
      const me = account.toLowerCase();
      const isOwner = ownerAddr ? ownerAddr.toLowerCase() === me : false;
      const isAdmin = isOwner || (adminAddr ? adminAddr.toLowerCase() === me : false);

      const userId = userIdBn.toNumber();
      if (userId === 0) {
        let usdtBalance = null;
        try { usdtBalance = provider ? await getUsdtBalance(provider, account) : null; } catch {}
        setData({ ...EMPTY, prices, usdtBalance, registered: false, isAdmin, isOwner, minWithdraw: minW });
        setLoading(false);
        inflight.current = false;
        return;
      }

      // Core reads — everything needed to render the dashboard, in parallel.
      const [d, b, pkgs, refs, club, elig, cg, cd, cgl, ...pkgDetails] = await Promise.all([
        read.getUserDetails(userId),
        read.getUserIncomeBreakdown(userId),
        read.getUserPackages(userId),
        read.getUserReferrals(userId),
        read.getClubInfo(),
        read.getEligibility(userId, 1),
        read.getUserClaimableClub(userId, 0),
        read.getUserClaimableClub(userId, 1),
        read.getUserClaimableClub(userId, 2),
        ...Array.from({ length: 12 }, (_, i) => read.getUserPackageDetails(userId, i + 1)),
      ]);

      let highestBoard = 0;
      for (let i = 0; i < 12; i++) if (pkgs[i]) highestBoard = i + 1;
      const directs = d.totalDirectReferrals.toNumber();
      const levelsUnlocked = Math.min(20, directs * 2);

      let usdtBalance = null;
      try { usdtBalance = provider ? await getUsdtBalance(provider, account) : null; } catch {}

      // ---- FIRST PAINT: render the dashboard now ----
      setData({
        ...EMPTY,
        userId,
        registered: true,
        isAdmin,
        isOwner,
        minWithdraw: minW,
        highestBoard,
        levelsUnlocked,
        details: {
          account: d.account,
          id: d.id.toNumber(),
          referrer: d.referrer.toNumber(),
          isActive: d.isActive,
          totalDirectReferrals: directs,
          claimableAmount: d.claimableAmount,
          totalClaimed: d.totalClaimed,
          totalIncome: d.totalIncome,
        },
        breakdown: {
          direct: b.direct, upgrade: b.upgrade, level: b.level, royalty: b.royalty,
          gold: b.gold, diamond: b.diamond, global: b.global,
          totalIncome: b.totalIncome, claimableAmount: b.claimableAmount, totalClaimed: b.totalClaimed,
        },
        packages: pkgs,
        packageDetails: pkgDetails,
        prices,
        referrals: refs.map((r) => r.toNumber()),
        clubInfo: club,
        eligibility: { royalty: elig.royalty, gold: elig.gold, diamond: elig.diamond, global: elig.global },
        claimableGold: cg,
        claimableDiamond: cd,
        claimableGlobal: cgl,
        usdtBalance,
      });
      setLoading(false); // <- UI is interactive here; the rest streams in

      // ---- BACKGROUND: held info (parallel) ----
      read &&
        Promise.all(Array.from({ length: 12 }, (_, i) => read.getHeldInfo(userId, i + 1)))
          .then((hs) =>
            setData((prev) => ({
              ...prev,
              heldLoaded: true,
              held: hs.map((h) => ({
                heldCount: h.heldCount.toNumber(),
                heldAmount: h.heldAmount,
                pending: h.pending,
                resolved: h.resolved,
              })),
            }))
          )
          .catch(() => setData((prev) => ({ ...prev, heldLoaded: true })));

      // ---- BACKGROUND: recent activity event scan ----
      scanActivities(userId)
        .then((activities) => setData((prev) => ({ ...prev, activities, activitiesLoaded: true })))
        .catch(() => setData((prev) => ({ ...prev, activitiesLoaded: true })));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Nexaura load failed:", e?.message || e);
    } finally {
      inflight.current = false;
      setLoading(false);
    }
  }, [account, read]); // eslint-disable-line

  // Reload when account or network changes, or on explicit refresh (no loops).
  useEffect(() => {
    load();
  }, [account, isCorrectNetwork, refreshKey]);

  // ---- Generic write runner (approval + tx + toast + reload) -------------
  const runTx = useCallback(
    async ({ needsApproval, approvalAmount, action, pending, success }) => {
      if (!write) {
        toast.error("Wallet not ready", "Connect your wallet to continue.");
        return false;
      }
      if (busy) return false;
      setBusy(true);
      try {
        if (needsApproval) {
          await ensureAllowance(getSigner(), account, getProvider(), approvalAmount, (m) =>
            toast.info("Approval", m)
          );
        }
        toast.info(pending || "Processing", "Confirm in your wallet…");
        const tx = await action();
        toast.info("Submitted", "Waiting for confirmation…");
        await tx.wait();
        toast.success("Success", success || "Transaction confirmed.");
        toast.celebrate();
        await load();
        return true;
      } catch (e) {
        toast.error("Failed", parseError(e));
        return false;
      } finally {
        setBusy(false);
      }
    },
    [write, busy, account, getSigner, getProvider, toast, load]
  );

  // ---- Actions -----------------------------------------------------------
  const register = useCallback(
    (referrerId) =>
      runTx({
        needsApproval: true,
        approvalAmount: data.prices[0],
        action: () => write.register(referrerId),
        pending: "Registering",
        success: "Welcome to Nexaura! Board 1 activated.",
      }),
    [runTx, write, data.prices]
  );

  const upgrade = useCallback(
    (boardId) =>
      runTx({
        needsApproval: true,
        approvalAmount: data.prices[boardId - 1],
        action: () => write.upgradePackage(boardId),
        pending: `Upgrading to Board ${boardId}`,
        success: `Board ${boardId} activated.`,
      }),
    [runTx, write, data.prices]
  );

  const retopup = useCallback(
    (boardId) =>
      runTx({
        needsApproval: true,
        approvalAmount: data.prices[boardId - 1],
        action: () => write.retopupPackage(boardId),
        pending: `Re-topping Board ${boardId}`,
        success: `Board ${boardId} ceiling extended.`,
      }),
    [runTx, write, data.prices]
  );

  const triggerAutoUpgrade = useCallback(
    (boardId) =>
      runTx({
        needsApproval: false,
        action: () => write.triggerAutoUpgrade(boardId),
        pending: "Triggering auto-upgrade",
        success: "Auto-upgrade complete.",
      }),
    [runTx, write]
  );

  const claimRoyalty = useCallback(
    (boardId) =>
      runTx({
        needsApproval: false,
        action: () => write.claimRoyalty(boardId),
        pending: "Claiming royalty",
        success: "Royalty added to your claimable balance.",
      }),
    [runTx, write]
  );

  const claimClub = useCallback(
    (club) => {
      const fn = club === 0 ? "claimGold" : club === 1 ? "claimDiamond" : "claimGlobal";
      const name = club === 0 ? "Gold" : club === 1 ? "Diamond" : "Global";
      return runTx({
        needsApproval: false,
        action: () => write[fn](),
        pending: `Claiming ${name} club`,
        success: `${name} reward added to your claimable balance.`,
      });
    },
    [runTx, write]
  );

  const claimAll = useCallback(
    () =>
      runTx({
        needsApproval: false,
        action: () => write.claimAll(),
        pending: "Claiming all rewards",
        success: "All available rewards consolidated.",
      }),
    [runTx, write]
  );

  const withdraw = useCallback(
    () =>
      runTx({
        needsApproval: false,
        action: () => write.withdraw(),
        pending: "Withdrawing",
        success: "Withdrawal sent to your wallet.",
      }),
    [runTx, write]
  );

  // Read helper used by Royalty screen (per-board claimable preview)
  const fetchClaimableRoyalty = useCallback(
    async (userId, boardId) => {
      try {
        return await read.getUserClaimableRoyalty(userId, boardId);
      } catch {
        return null;
      }
    },
    [read]
  );

  const fetchRoyaltyInfo = useCallback(
    async (boardId) => {
      try {
        return await read.getRoyaltyInfo(boardId);
      } catch {
        return null;
      }
    },
    [read]
  );

  const fetchEligibilityForBoard = useCallback(
    async (userId, boardId) => {
      try {
        return await read.getEligibility(userId, boardId);
      } catch {
        return null;
      }
    },
    [read]
  );

  // Team: fetch direct referrals of any user id (for level traversal).
  const fetchReferrals = useCallback(
    async (uid) => {
      try {
        const r = await read.getUserReferrals(uid);
        return r.map((x) => x.toNumber());
      } catch {
        return [];
      }
    },
    [read]
  );

  // Team: which boards a user is active on (for per-package filtering).
  const fetchUserPackages = useCallback(
    async (uid) => {
      try {
        return await read.getUserPackages(uid); // bool[12]
      } catch {
        return Array(12).fill(false);
      }
    },
    [read]
  );

  // Admin: read pools + config in one shot for the panel.
  const fetchAdminData = useCallback(async () => {
    try {
      const [pools, cap, depth, club, minW] = await Promise.all([
        read.getPoolBalances(),
        read.cascadeCap(),
        read.rollupDepth(),
        read.getClubInfo(),
        read.minWithdraw().catch(() => null),
      ]);
      const royalty = [];
      for (let b = 3; b <= 12; b++) royalty.push({ board: b, info: await read.getRoyaltyInfo(b) });
      return {
        pools: { missing: pools.missing, gold: pools.gold, diamond: pools.diamond, global: pools.global },
        config: {
          cascadeCap: cap.toNumber(),
          rollupDepth: depth.toNumber(),
          minWithdraw: minW,
        },
        club,
        royalty,
      };
    } catch (e) {
      return null;
    }
  }, [read]);

  // ---- Admin actions -----------------------------------------------------
  const adminSetRoyaltyRound = useCallback(
    (boardId, amountHuman) =>
      runTx({
        needsApproval: false,
        action: () => write.setRoyaltyRound(boardId, parseUsdt(amountHuman)),
        pending: `Setting royalty round (Board ${boardId})`,
        success: "Royalty round created.",
      }),
    [runTx, write]
  );

  const adminSetClubRound = useCallback(
    (club, amountHuman) => {
      const fn = club === 0 ? "setGoldRound" : club === 1 ? "setDiamondRound" : "setGlobalRound";
      const name = club === 0 ? "Gold" : club === 1 ? "Diamond" : "Global";
      return runTx({
        needsApproval: false,
        action: () => write[fn](parseUsdt(amountHuman)),
        pending: `Setting ${name} round`,
        success: `${name} round created.`,
      });
    },
    [runTx, write]
  );

  // Missing pool is sent to the connected admin (no recipient arg in the contract).
  const adminClaimMissingPool = useCallback(
    () =>
      runTx({
        needsApproval: false,
        action: () => write.claimMissingPoolFull(),
        pending: "Withdrawing missing pool",
        success: "Missing pool sent to your wallet.",
      }),
    [runTx, write]
  );

  const adminDistributeAllRoyalty = useCallback(
    (percent) =>
      runTx({
        needsApproval: false,
        action: () => write.distributeAllRoyalty(Math.round(Number(percent) * 100)), // % -> bps
        pending: "Distributing royalty",
        success: "Royalty distributed to all boards.",
      }),
    [runTx, write]
  );

  const adminResetRoyaltyFund = useCallback(
    (boardId) =>
      runTx({
        needsApproval: false,
        action: () => write.resetRoyaltyFund(boardId),
        pending: `Resetting royalty fund (Board ${boardId})`,
        success: "Royalty fund reset.",
      }),
    [runTx, write]
  );

  const adminResetAllRoyaltyFunds = useCallback(
    () =>
      runTx({
        needsApproval: false,
        action: () => write.resetAllRoyaltyFunds(),
        pending: "Resetting all royalty funds",
        success: "All royalty funds reset.",
      }),
    [runTx, write]
  );

  const adminResetClubFund = useCallback(
    (club) => {
      const fn = club === 0 ? "resetGoldFund" : club === 1 ? "resetDiamondFund" : "resetGlobalFund";
      const name = club === 0 ? "Gold" : club === 1 ? "Diamond" : "Global";
      return runTx({
        needsApproval: false,
        action: () => write[fn](),
        pending: `Resetting ${name} fund`,
        success: `${name} fund reset.`,
      });
    },
    [runTx, write]
  );

  const adminEmergencyWithdraw = useCallback(
    (amountHuman) =>
      runTx({
        needsApproval: false,
        action: () => write.emergencyWithdraw(parseUsdt(amountHuman)),
        pending: "Emergency withdraw",
        success: "Emergency withdrawal sent to your wallet.",
      }),
    [runTx, write]
  );

  const adminSetConfig = useCallback(
    (kind, value) => {
      const map = {
        cascadeCap: () => write.setCascadeCap(value),
        rollupDepth: () => write.setRollupDepth(value),
      };
      return runTx({
        needsApproval: false,
        action: map[kind],
        pending: "Updating config",
        success: "Config updated.",
      });
    },
    [runTx, write]
  );

  const adminActivateBoard = useCallback(
    (uid, boardId) =>
      runTx({
        needsApproval: false,
        action: () => write.adminActivateBoard(uid, boardId),
        pending: `Activating board ${boardId} for #${uid}`,
        success: "Board activated.",
      }),
    [runTx, write]
  );

  const adminSetRoyaltyOverride = useCallback(
    (uid, boardId, val) =>
      runTx({
        needsApproval: false,
        action: () => write.adminSetRoyaltyOverride(uid, boardId, val),
        pending: "Setting royalty override",
        success: "Override updated.",
      }),
    [runTx, write]
  );

  const adminSetMinWithdraw = useCallback(
    (amountHuman) =>
      runTx({
        needsApproval: false,
        action: () => write.setMinWithdraw(parseUsdt(amountHuman)),
        pending: "Updating minimum withdrawal",
        success: "Minimum withdrawal updated.",
      }),
    [runTx, write]
  );

  const adminRegister = useCallback(
    (addr, sponsorId) =>
      runTx({
        needsApproval: false,
        action: () => write.adminRegister(addr, sponsorId || 0),
        pending: "Registering user",
        success: "User registered (free).",
      }),
    [runTx, write]
  );

  // User lookup: resolve an address to an id (0 if none).
  const fetchIdByAddress = useCallback(
    async (addr) => {
      try {
        return (await read.addressToId(addr)).toNumber();
      } catch {
        return 0;
      }
    },
    [read]
  );

  // User lookup: full snapshot for any user id.
  const fetchUserAll = useCallback(
    async (uid) => {
      try {
        const [d, b, pkgs, elig] = await Promise.all([
          read.getUserDetails(uid),
          read.getUserIncomeBreakdown(uid),
          read.getUserPackages(uid),
          read.getEligibility(uid, 1),
        ]);
        return {
          details: {
            account: d.account,
            id: d.id.toNumber(),
            referrer: d.referrer.toNumber(),
            isActive: d.isActive,
            totalDirectReferrals: d.totalDirectReferrals.toNumber(),
            claimableAmount: d.claimableAmount,
            totalClaimed: d.totalClaimed,
            totalIncome: d.totalIncome,
          },
          breakdown: {
            direct: b.direct, upgrade: b.upgrade, level: b.level, royalty: b.royalty,
            gold: b.gold, diamond: b.diamond, global: b.global,
            totalIncome: b.totalIncome, claimableAmount: b.claimableAmount, totalClaimed: b.totalClaimed,
          },
          packages: pkgs,
          eligibility: { gold: elig.gold, diamond: elig.diamond, global: elig.global },
        };
      } catch {
        return null;
      }
    },
    [read]
  );

  return {
    data,
    loading,
    busy,
    reload: load,
    refresh,
    register,
    upgrade,
    retopup,
    triggerAutoUpgrade,
    claimRoyalty,
    claimClub,
    claimAll,
    withdraw,
    fetchClaimableRoyalty,
    fetchRoyaltyInfo,
    fetchEligibilityForBoard,
    fetchReferrals,
    fetchUserPackages,
    fetchAdminData,
    adminSetRoyaltyRound,
    adminSetClubRound,
    adminClaimMissingPool,
    adminDistributeAllRoyalty,
    adminResetRoyaltyFund,
    adminResetAllRoyaltyFunds,
    adminResetClubFund,
    adminEmergencyWithdraw,
    adminSetConfig,
    adminActivateBoard,
    adminSetRoyaltyOverride,
    adminSetMinWithdraw,
    adminRegister,
    fetchIdByAddress,
    fetchUserAll,
  };
}
